import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '~/components/ui/Button';
import { Link } from '~/components/ui/Link';
import { PricingSettingSectionWrapper } from '~/components/ui/pricing/common';
import { reloadTokens } from '~/lib/.client/api';
import { HELP_CENTER_URL } from '~/lib/urls';
import type { OrganizationData } from '~/types/organization';
import { isSafari } from '~/utils/detectBrowser';
import { withSpinner } from '~/utils/withSpinner';

interface Props {
  organization?: OrganizationData;
  needCompact?: boolean;
}

export const TokenReloadSettingsSection = ({ organization, needCompact }: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const onReloadTokens = useCallback(async () => {
    await withSpinner({
      action: async () => {
        const result = await reloadTokens(organization);

        if ('error' in result) {
          toast.error(result.error);
          return;
        }

        if (isSafari()) {
          window.location.href = result.sessionUrl;
        } else {
          window.open(result.sessionUrl, '_blank');
        }
      },
      onStateUpdate: setIsLoading,
    });
  }, []);

  if (needCompact) {
    return (
      <p className="w-full text-pretty items-center text-sm bg-bolt-ds-surfaceOne rounded-[7px] p-6 border border-bolt-elements-borderColor">
        Want additional tokens without upgrading your plan? Purchase a{' '}
        <Link
          className="text-sm decoration-none text-bolt-ds-textPrimary hover:text-bolt-ds-textPrimary hover:decoration-underline"
          href={`${HELP_CENTER_URL}/account-and-subscription/tokens#buy-more-tokens`}
          target="_blank"
        >
          token reload
        </Link>
        {'\u00A0'}
        <Button className="flex-inline" variant="link-color" onClick={onReloadTokens} loading={isLoading}>
          here
        </Button>
        .
      </p>
    );
  }

  return (
    <PricingSettingSectionWrapper className="flex flex-col md:flex-row gap-6 items-start">
      <div className="flex flex-col items-start gap-2">
        <span className="font-semibold text-lg">Running out of tokens?</span>

        <span className="text-bolt-ds-textSecondary text-sm">
          Purchase token reload so you can get back to prompting immediately.
        </span>
      </div>

      <Button onClick={onReloadTokens} loading={isLoading}>
        Reload tokens
      </Button>
    </PricingSettingSectionWrapper>
  );
};
